import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowSquareOut, CalendarBlank, Clock, MapPin, Eye, EyeSlash, DownloadSimple, DeviceMobile, Trophy, Plus } from "@phosphor-icons/react";
import { Header, DivisionTabs, Modal, In, Sel } from "./shared";
import { cacheRefData } from "@/lib/offlineStore";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

const fmtTime = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

export default function ScoreKeeperTab() {
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [readyMatches, setReadyMatches] = useState([]);
  const [division, setDivision] = useState("Open");
  const [opened, setOpened] = useState("");
  const [hideCompleted, setHideCompleted] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [showPlayoffModal, setShowPlayoffModal] = useState(false);
  const { canInstall, installed, promptInstall } = useInstallPrompt();

  const load = useCallback(() => {
    api.get("/games").then((r) => setGames(r.data));
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/playoffs/ready-matches").then((r) => setReadyMatches(r.data || [])).catch(() => setReadyMatches([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Pre-download every game/team/player so any game can be scored later with no connection.
  const prepareOffline = async () => {
    setPreparing(true);
    try {
      const [g, t, p] = await Promise.all([api.get("/games"), api.get("/teams"), api.get("/players")]);
      cacheRefData({ games: g.data, teams: t.data, players: p.data });
      setGames(g.data); setTeams(t.data);
      toast.success(`${g.data.length} game${g.data.length === 1 ? "" : "s"} saved for offline scoring on this device`);
    } catch {
      toast.error("Couldn't reach the server — connect to the internet, then try again.");
    } finally { setPreparing(false); }
  };

  const teamOf = (id) => teams.find((t) => t.id === id);
  const divisionOf = (id) => teams.find((t) => t.id === id)?.division || "Open";
  const gameDivision = (g) => g.division || divisionOf(g.home_team_id);
  const playoffGames = games.filter((g) => g.is_playoff && gameDivision(g) === division)
    .sort((x, y) => new Date(x.date) - new Date(y.date));
  const divisionReady = readyMatches.filter((m) => m.division === division && !m.game_id);
  const divisionGames = games.filter((g) => !g.is_playoff && divisionOf(g.home_team_id) === division);
  const completedCount = divisionGames.filter((g) => g.status === "completed").length;
  const shownGames = hideCompleted ? divisionGames.filter((g) => g.status !== "completed") : divisionGames;

  // Group games into weekly sections (numeric weeks ascending; unscheduled last).
  const byWeek = {};
  shownGames.forEach((g) => {
    const key = g.week == null || g.week === "" ? "none" : String(g.week);
    (byWeek[key] = byWeek[key] || []).push(g);
  });
  const weekKeys = Object.keys(byWeek).sort((a, b) => {
    if (a === "none") return 1;
    if (b === "none") return -1;
    return Number(a) - Number(b);
  });
  Object.values(byWeek).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0) || new Date(a.date) - new Date(b.date)));

  const openGame = (id) => {
    setOpened(id);
    window.open(`/admin/score/${id}`, "_blank", "noopener");
  };

  const TeamBadge = ({ id }) => {
    const t = teamOf(id);
    return (
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-9 h-9 shrink-0 flex items-center justify-center text-black font-display text-base overflow-hidden" style={{ background: t?.primary_color || "#FF5722" }}>
          {t?.logo_url ? <img src={t.logo_url} alt="" className="w-full h-full object-cover" /> : (t?.name?.charAt(0) || "?")}
        </span>
        <span className="font-display text-lg uppercase truncate">{t?.name || "TBD"}</span>
      </div>
    );
  };

  const GameTile = ({ g }) => {
    const done = g.status === "completed";
    const isOpened = opened === g.id;
    const time = fmtTime(g.date);
    const court = (g.court || "").trim();
    return (
      <button
        key={g.id}
        onClick={() => openGame(g.id)}
        data-testid={`scorekeeper-game-tile-${g.id}`}
        className={`group text-left border p-5 transition-all duration-200 active:scale-[0.98] ${
          isOpened
            ? "border-[#FF5722] bg-[#FF5722]/10 shadow-[0_0_20px_rgba(255,87,34,0.3)]"
            : "border-white/10 bg-[#121212] hover:border-[#FF5722] hover:bg-white/[0.03]"
        }`}
      >
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 ${done ? "bg-[#00E676]/20 text-[#00E676]" : "bg-[#FFEA00]/15 text-[#FFEA00]"}`}>
              {done ? "Final" : "Scheduled"}
            </span>
            {g.is_playoff && (
              <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-[#FF5722]/20 text-[#FF5722]" data-testid={`scorekeeper-playoff-badge-${g.id}`}>
                {g.playoff_round || "Playoff"}
              </span>
            )}
          </div>
          {time && (
            <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#FF5722] shrink-0" data-testid={`scorekeeper-game-time-${g.id}`}>
              <Clock size={13} weight="fill" /> {time}
            </span>
          )}
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <TeamBadge id={g.home_team_id} />
            {done && <span className="font-display text-2xl text-[#FF5722] tabular-nums shrink-0">{g.home_score}</span>}
          </div>
          <div className="text-white/30 text-xs font-bold uppercase tracking-widest text-center">vs</div>
          <div className="flex items-center justify-between gap-2">
            <TeamBadge id={g.away_team_id} />
            {done && <span className="font-display text-2xl text-[#FF5722] tabular-nums shrink-0">{g.away_score}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-white/5 text-white/40 text-xs flex-wrap">
          <span className="flex items-center gap-1.5"><CalendarBlank size={14} />{new Date(g.date).toLocaleDateString()}</span>
          {time && <span className="flex items-center gap-1.5 text-white/60"><Clock size={14} />{time}</span>}
          {court && <span className="flex items-center gap-1.5 text-[#FF5722]"><MapPin size={14} weight="fill" />{court}</span>}
          {g.venue ? <span className="truncate">· {g.venue}</span> : null}
          <span className="flex items-center gap-1 ml-auto text-white/30 group-hover:text-[#FF5722] transition-colors">Open <ArrowSquareOut size={13} /></span>
        </div>
      </button>
    );
  };

  return (
    <div data-testid="scorekeeper-tab" className="max-w-5xl">
      <Header title="Score Keeper" action={(
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {canInstall && !installed && (
            <button onClick={promptInstall} data-testid="scorekeeper-install-app"
              className="flex items-center gap-2 border border-[#00E676]/50 bg-[#00E676]/10 text-[#00E676] font-bold uppercase tracking-wide px-4 py-2.5 text-sm hover:bg-[#00E676]/20 transition-all duration-200">
              <DeviceMobile size={16} weight="fill" /> Install App
            </button>
          )}
          <button onClick={prepareOffline} disabled={preparing} data-testid="scorekeeper-prepare-offline"
            className="flex items-center gap-2 border border-[#FF5722]/50 bg-[#FF5722]/10 text-[#FF5722] font-bold uppercase tracking-wide px-4 py-2.5 text-sm hover:bg-[#FF5722]/20 disabled:opacity-50 transition-all duration-200">
            <DownloadSimple size={16} weight="fill" /> {preparing ? "Saving…" : "Save for Offline"}
          </button>
          {completedCount > 0 && (
            <button onClick={() => setHideCompleted((v) => !v)} data-testid="scorekeeper-toggle-completed"
              title={hideCompleted ? "Show completed games" : "Hide all completed games"}
              className={`flex items-center gap-2 border font-bold uppercase tracking-wide px-4 py-2.5 text-sm transition-all duration-200 ${hideCompleted ? "border-[#FF5722] text-[#FF5722] bg-[#FF5722]/10" : "border-white/15 text-white/70 hover:text-white hover:border-white/40"}`}>
              {hideCompleted ? <><Eye size={16} /> Show Completed ({completedCount})</> : <><EyeSlash size={16} /> Hide Completed ({completedCount})</>}
            </button>
          )}
        </div>
      )} />
      <p className="text-white/50 text-sm -mt-4 mb-6">Pick a game to start keeping score. It opens in a new tab with a large, distraction-free scoreboard. Tip: tap <span className="text-[#FF5722] font-semibold">Save for Offline</span> while online so you can score any game later with no connection.</p>

      <DivisionTabs value={division} onChange={setDivision} prefix="scorekeeper" />

      {(playoffGames.length > 0 || divisionReady.length > 0) && (
        <section data-testid="scorekeeper-playoffs-section" className="border-2 border-[#FF5722]/50 bg-[#0D0D0D] p-4 sm:p-5 mb-6">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10 flex-wrap gap-2">
            <h3 className="font-display text-2xl uppercase text-[#FF5722] flex items-center gap-2">
              <Trophy size={22} weight="fill" /> Playoffs
            </h3>
            <div className="flex items-center gap-3">
              {divisionReady.length > 0 && (
                <button onClick={() => setShowPlayoffModal(true)} data-testid="scorekeeper-new-playoff-game"
                  className="flex items-center gap-1.5 bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide px-4 py-2 text-xs transition-all duration-200">
                  <Plus size={14} weight="bold" /> New Playoff Game
                </button>
              )}
              <span className="text-[11px] uppercase font-bold tracking-wide text-white/40">{playoffGames.length} game{playoffGames.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          {playoffGames.length === 0 ? (
            <p className="text-white/40 text-sm" data-testid="scorekeeper-playoffs-empty">No playoff games in the Scorekeeper yet — tap “New Playoff Game” to pick a matchup and set its date, time & court.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {playoffGames.map((g) => <GameTile key={g.id} g={g} />)}
            </div>
          )}
        </section>
      )}

      {divisionGames.length === 0 && playoffGames.length === 0 && divisionReady.length === 0 ? (
        <div className="border border-white/10 bg-[#121212] p-10 text-center text-white/40" data-testid="scorekeeper-no-games">
          No games in the {division} division yet. Add games in the Schedule tab.
        </div>
      ) : divisionGames.length > 0 && weekKeys.length === 0 ? (
        <div className="border border-white/10 bg-[#121212] p-10 text-center text-white/40" data-testid="scorekeeper-all-hidden">
          All {completedCount} completed game{completedCount === 1 ? "" : "s"} are hidden. Tap “Show Completed” to view them.
        </div>
      ) : (
        <div className="space-y-6">
          {weekKeys.map((wk) => {
            const list = byWeek[wk];
            return (
              <section key={wk} data-testid={`scorekeeper-week-${wk}`} className="border-2 border-white/15 bg-[#0D0D0D] p-4 sm:p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                  <h3 className="font-display text-2xl uppercase text-[#FF5722]">
                    {wk === "none" ? "Unscheduled" : `Week ${wk}`}
                  </h3>
                  <span className="text-[11px] uppercase font-bold tracking-wide text-white/40">{list.length} game{list.length === 1 ? "" : "s"}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map((g) => <GameTile key={g.id} g={g} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {showPlayoffModal && (
        <NewPlayoffGameModal
          matches={divisionReady}
          onClose={() => setShowPlayoffModal(false)}
          onCreated={(gameId, openAfter) => {
            setShowPlayoffModal(false);
            load();
            if (openAfter) openGame(gameId);
          }}
        />
      )}
    </div>
  );
}

function NewPlayoffGameModal({ matches, onClose, onCreated }) {
  const [matchId, setMatchId] = useState(matches[0]?.match_id || "");
  const [date, setDate] = useState("");
  const [court, setCourt] = useState("");
  const [venue, setVenue] = useState("");
  const [busy, setBusy] = useState(false);

  const label = (m) => `${m.round_name}: #${m.a_seed || "?"} ${m.a_name} vs #${m.b_seed || "?"} ${m.b_name}`;

  const create = async (openAfter) => {
    const m = matches.find((x) => x.match_id === matchId);
    if (!m) { toast.error("Pick a matchup"); return; }
    if (!date) { toast.error("Set the date & time"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/playoffs/${encodeURIComponent(m.division)}/match/${m.match_id}/game`, {
        date: new Date(date).toISOString(), court, venue,
      });
      toast.success("Playoff game added to the Scorekeeper — the bracket will update automatically");
      onCreated(r.data.game_id, openAfter);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="New Playoff Game">
      <div className="space-y-4" data-testid="scorekeeper-playoff-modal">
        <Sel label="Playoff matchup" value={matchId} onChange={(e) => setMatchId(e.target.value)} data-testid="scorekeeper-playoff-matchup-select">
          {matches.map((m) => <option key={m.match_id} value={m.match_id}>{label(m)}</option>)}
        </Sel>
        <In label="Date & time" type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="scorekeeper-playoff-date" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <In label="Court / Gym" type="text" value={court} onChange={(e) => setCourt(e.target.value)} placeholder="e.g. Court 1" data-testid="scorekeeper-playoff-court" />
          <In label="Venue (optional)" type="text" value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="e.g. IEFSC Main Gym" data-testid="scorekeeper-playoff-venue" />
        </div>
        <p className="text-white/40 text-xs">Only matchups whose teams are decided appear here. The final score entered in the Scorekeeper updates the playoff bracket and advances the winner automatically.</p>
        <div className="flex gap-3 pt-1">
          <button onClick={() => create(false)} disabled={busy} data-testid="scorekeeper-playoff-create-btn" className="flex-1 border border-white/20 text-white/80 hover:bg-white/5 disabled:opacity-50 font-bold uppercase tracking-wide py-2.5 text-sm transition-colors">
            Create
          </button>
          <button onClick={() => create(true)} disabled={busy} data-testid="scorekeeper-playoff-create-open-btn" className="flex-1 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-2.5 text-sm transition-all duration-200">
            {busy ? "Creating…" : "Create & Score Now"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
